import errorMonitoring from '@/errorMonitoring';

/**
 * Everything the funnel knows about `siempreplus-widget`'s internals, in one place.
 *
 * The widget exposes no imperative way to open its sign-in, so the checkout CTA drives it through
 * markup and a pub/sub topic that are not part of its props contract. A widget refactor that renames
 * either would quietly degrade the CTA to revealing the raw widget; keeping them here makes that one
 * file to fix, and the fallback reports itself so it does not go unnoticed. A public open API in the
 * widget is the follow-up that retires this module.
 */

/** The widget's own sign-in trigger, as `SignInLink` draws it. */
const SIGN_IN_TRIGGER_SELECTOR = 'button.signin-button';

/** A looser fallback, tried only after the widget has been told to sign out. */
const FALLBACK_TRIGGER_SELECTOR = `${SIGN_IN_TRIGGER_SELECTOR}, button, [role="button"]`;

/**
 * The topic the widget's `UserInfo` listens on to sign itself out. Gontijo's name for it comes from
 * the widget's `getPubSubEventName('gontijo').signOut`.
 */
const WIDGET_SIGN_OUT_EVENT = 'gontijo-signOut';

/** The pub/sub the widget puts on `window`; the funnel has no global typing for it. */
type WidgetPubSubWindow = Window & {
  reservamosPubSub?: { pub: (topic: string, value?: unknown) => void };
};

/**
 * Take the triggers inside a hidden host out of the tab order and the accessibility tree, or put
 * them back.
 * @param {HTMLElement} host - The widget host.
 * @param {boolean} hidden - Whether the host is hidden.
 */
function syncTriggerAccessibility(host: HTMLElement, hidden: boolean) {
  host.querySelectorAll<HTMLElement>(SIGN_IN_TRIGGER_SELECTOR).forEach((trigger) => {
    if (hidden) {
      if (trigger.getAttribute('tabindex') !== '-1') trigger.setAttribute('tabindex', '-1');
      if (trigger.getAttribute('aria-hidden') !== 'true')
        trigger.setAttribute('aria-hidden', 'true');
    } else {
      trigger.removeAttribute('tabindex');
      trigger.removeAttribute('aria-hidden');
    }
  });
}

/**
 * Keep a hidden host's triggers hidden while the widget mounts late and redraws them. Only child
 * changes, and only the host's small subtree: the attributes set here do not re-trigger it.
 * @param {HTMLElement} host - The hidden widget host.
 * @returns {() => void} Disconnects the observer.
 */
function observeHiddenTriggers(host: HTMLElement): () => void {
  syncTriggerAccessibility(host, true);
  const observer = new MutationObserver(() => syncTriggerAccessibility(host, true));
  observer.observe(host, { childList: true, subtree: true });

  return () => observer.disconnect();
}

let fallbackReported = false;

/**
 * Report, once per page, that the sign-in trigger could not be found and the raw widget was shown.
 * Carries the host's id and nothing about the buyer.
 * @param {string} hostId - The widget's mount id.
 */
function reportFallback(hostId: string) {
  if (fallbackReported) return;
  fallbackReported = true;

  errorMonitoring.notify({
    error: new Error('Gontijo widget sign-in trigger not found; revealed the widget instead'),
    info: {
      from: 'widgetBridge.openWidgetSignIn',
      hostId,
      selector: SIGN_IN_TRIGGER_SELECTOR,
    },
  });
}

/** Test-only: forget that the fallback was reported. */
function resetFallbackReportForTests() {
  fallbackReported = false;
}

interface OpenWidgetSignInOptions {
  /** The widget's mount id, for the report. */
  hostId: string;
  /** Called right before a trigger is clicked. */
  onOpen?: () => void;
  /** Called when no trigger turned up; the caller reveals the raw widget. */
  onFallback: () => void;
}

/**
 * Open the widget's sign-in: click its trigger, or, if it is missing — likeliest because the widget
 * still greets a user the funnel has signed out — publish the sign-out and try again on the next
 * frame. If that fails too, report it and hand over to `onFallback`.
 * @param {() => (HTMLElement|null)} getHost - Reads the host, at click time and again a frame later.
 * @param {OpenWidgetSignInOptions} options - Callbacks and the host's id.
 * @returns {() => void} Cancels the pending retry, if any.
 */
function openWidgetSignIn(
  getHost: () => HTMLElement | null,
  { hostId, onOpen, onFallback }: OpenWidgetSignInOptions,
): () => void {
  function clickTrigger(selector: string) {
    const trigger = getHost()?.querySelector<HTMLElement>(selector);
    if (!trigger) return false;

    onOpen?.();
    trigger.click();
    return true;
  }

  if (clickTrigger(SIGN_IN_TRIGGER_SELECTOR)) return () => {};

  // A stale widget: bring it in line with the funnel, and give it a frame to redraw.
  (window as WidgetPubSubWindow).reservamosPubSub?.pub(WIDGET_SIGN_OUT_EVENT);

  const frame = window.requestAnimationFrame(() => {
    if (clickTrigger(FALLBACK_TRIGGER_SELECTOR)) return;

    reportFallback(hostId);
    onFallback();
  });

  return () => window.cancelAnimationFrame(frame);
}

export type { WidgetPubSubWindow };
export {
  SIGN_IN_TRIGGER_SELECTOR,
  FALLBACK_TRIGGER_SELECTOR,
  WIDGET_SIGN_OUT_EVENT,
  syncTriggerAccessibility,
  observeHiddenTriggers,
  resetFallbackReportForTests,
  openWidgetSignIn,
};
